// src/pages/TicketDesignTemplate/modals/CreateTicketDesignTemplateModal.tsx
import React, { useEffect, useRef, useState } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  FormFeedback,
  Spinner,
} from 'reactstrap';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { PageActionPermission } from 'services/pageActionPermission/PageActionPermission';
import { createTicketDesignTemplate, updateTicketDesignTemplateLayout } from 'slices/ticketDesignTemplate/thunk';
import { DESIGN_UNIT_OPTIONS } from '../types';
import FileUploadPreview from '../components/FileUploadPreview';
import TicketLayoutEditor from '../components/TicketLayoutEditor';
import { toast } from 'react-toastify';
import { useScreenText } from 'helpers/screenText';

interface CreateTicketDesignTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const CreateTicketDesignTemplateModal: React.FC<CreateTicketDesignTemplateModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const dispatch = useDispatch();
  const location = useLocation();
  // Text hiển thị lấy từ src/locales/screens.json (mục "ticket_design_template" / "common")
  const st = useScreenText('ticket_design_template');
  const common = useScreenText('common');
  const { submitting } = useSelector((state: any) => state.TicketDesignTemplate);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);
  const [layoutPreviewOpen, setLayoutPreviewOpen] = useState(false);
  const [layoutJson, setLayoutJson] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);

  // Thu hồi blob URL khi component unmount để tránh rò rỉ bộ nhớ
  useEffect(() => {
    return () => {
      if (previewImageUrl) {
        URL.revokeObjectURL(previewImageUrl);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Validation schema
  const validationSchema = Yup.object({
    templateName: Yup.string()
      .required(st('validation_template_name_required'))
      .max(200, st('validation_template_name_max')),
    designUnit: Yup.string()
      .oneOf(['Internal', 'External'], st('validation_design_unit_invalid'))
      .required(st('validation_design_unit_required')),
    issuancePeriod: Yup.string()
      .required(st('validation_issuance_period_required'))
      .matches(
        /^K\d+-\d{2}\/\d{4}$/,
        st('validation_issuance_period_format')
      ),
  });

  // Formik
  const formik = useFormik({
    initialValues: {
      templateName: '',
      designUnit: 'Internal' as 'Internal' | 'External',
      issuancePeriod: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      // File thiết kế là bắt buộc khi tạo mới
      if (!selectedFile) {
        setFileError(st('validation_file_required'));
        return;
      }

      try {
        // Tạo FormData để gửi file + form data
        const formData = new FormData();

        // Thêm các fields text vào FormData
        formData.append('templateName', values.templateName);
        formData.append('designUnit', values.designUnit);
        formData.append('issuancePeriod', values.issuancePeriod);
        formData.append('file', selectedFile);

        const result = await dispatch(createTicketDesignTemplate(formData) as any);

        if (createTicketDesignTemplate.fulfilled.match(result)) {
          toast.success(st('toast_create_success'));

          // Nếu người dùng đã chỉnh bố cục trong popup xem trước thì lưu luôn theo mẫu vừa tạo
          const newTemplateId = result.payload;
          if (layoutJson && typeof newTemplateId === 'number') {
            const layoutResult = await dispatch(updateTicketDesignTemplateLayout({ id: newTemplateId, layoutJson }) as any);
            if (!updateTicketDesignTemplateLayout.fulfilled.match(layoutResult)) {
              toast.warning(st('toast_layout_save_failed'));
            }
          }

          onSuccess();
          handleClose();
        } else {
          const errorMessage =
            typeof result.payload === 'string'
              ? result.payload
              : st('toast_create_failed');
          toast.error(errorMessage);
        }
      } catch (error) {
        toast.error(st('toast_create_failed'));
      }
    },
  });

  // Handle file selection
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        toast.error(st('error_file_type'));
        return;
      }

      // Validate file size (max 50MB)
      const maxSize = 50 * 1024 * 1024; // 50MB in bytes
      if (file.size > maxSize) {
        toast.error(st('error_file_size'));
        return;
      }

      setSelectedFile(file);
      setFileError(null);

      // Thu hồi blob URL cũ (nếu có) trước khi tạo URL mới
      if (previewImageUrl) {
        URL.revokeObjectURL(previewImageUrl);
      }

      setLayoutJson(null);

      // Nếu là file ảnh thì tự động mở popup xem trước bố cục (kéo-thả mã vé, QR...)
      if (file.type.startsWith('image/')) {
        const blobUrl = URL.createObjectURL(file);
        setPreviewImageUrl(blobUrl);
        setLayoutPreviewOpen(true);
      } else {
        setPreviewImageUrl(null);
      }
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (previewImageUrl) {
      URL.revokeObjectURL(previewImageUrl);
    }
    setPreviewImageUrl(null);
    setLayoutPreviewOpen(false);
    setLayoutJson(null);
  };

  // Handle close
  const handleClose = () => {
    formik.resetForm();
    handleRemoveFile();
    setFileError(null);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} toggle={handleClose} size="lg">
      <ModalHeader toggle={handleClose}>{st('create_modal_title')}</ModalHeader>
      <Form onSubmit={formik.handleSubmit}>
        <ModalBody>
          <FormGroup>
            <Label for="templateName">
              {st('template_name_label')} <span className="text-danger">*</span>
            </Label>
            <Input
              type="text"
              id="templateName"
              name="templateName"
              placeholder={st('template_name_placeholder')}
              maxLength={200}
              value={formik.values.templateName}
              data-action-code="create-input-template-name"
              data-action-name="Tên bộ mẫu (Thêm mới)"
              disabled={!PageActionPermission.canEnable(location.pathname, "create-input-template-name")}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              invalid={formik.touched.templateName && !!formik.errors.templateName}
            />
            {formik.touched.templateName && formik.errors.templateName && (
              <FormFeedback>{formik.errors.templateName}</FormFeedback>
            )}
          </FormGroup>

          <FormGroup>
            <Label for="designUnit">
              {st('design_unit_label')} <span className="text-danger">*</span>
            </Label>
            <Input
              type="select"
              id="designUnit"
              name="designUnit"
              value={formik.values.designUnit}
              data-action-code="create-input-design-unit"
              data-action-name="Đơn vị thiết kế (Thêm mới)"
              disabled={!PageActionPermission.canEnable(location.pathname, "create-input-design-unit")}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              invalid={formik.touched.designUnit && !!formik.errors.designUnit}
            >
              {DESIGN_UNIT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Input>
            {formik.touched.designUnit && formik.errors.designUnit && (
              <FormFeedback>{formik.errors.designUnit}</FormFeedback>
            )}
          </FormGroup>

          <FormGroup>
            <Label for="issuancePeriod">
              {st('issuance_period_label')} <span className="text-danger">*</span>
            </Label>
            <Input
              type="text"
              id="issuancePeriod"
              name="issuancePeriod"
              placeholder={st('issuance_period_placeholder')}
              maxLength={20}
              value={formik.values.issuancePeriod}
              data-action-code="create-input-issuance-period"
              data-action-name="Kỳ áp dụng (Thêm mới)"
              disabled={!PageActionPermission.canEnable(location.pathname, "create-input-issuance-period")}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              invalid={formik.touched.issuancePeriod && !!formik.errors.issuancePeriod}
            />
            {formik.touched.issuancePeriod && formik.errors.issuancePeriod && (
              <FormFeedback>{formik.errors.issuancePeriod}</FormFeedback>
            )}
            <small className="text-muted">{st('issuance_period_hint')}</small>
          </FormGroup>

          <FormGroup>
            <Label for="file">
              {st('design_file_label')} <span className="text-danger">*</span>
            </Label>
            <Input
              innerRef={fileInputRef}
              type="file"
              id="file"
              accept=".png,.jpg,.jpeg,.pdf"
              invalid={!!fileError}
              data-action-code="create-input-file"
              data-action-name="File thiết kế vé (Thêm mới)"
              disabled={!PageActionPermission.canEnable(location.pathname, "create-input-file")}
              onChange={handleFileChange}
            />
            {fileError && <FormFeedback className="d-block">{fileError}</FormFeedback>}
            <small className="text-muted">{st('design_file_hint')}</small>
            {selectedFile && (
              <FileUploadPreview file={selectedFile} onRemove={handleRemoveFile} />
            )}
            {previewImageUrl && PageActionPermission.canDisplay(location.pathname, "button-preview-layout") && (
              <Button
                color="primary"
                outline
                size="sm"
                className="mt-2"
                type="button"
                data-action-code="button-preview-layout"
                data-action-name="Xem trước bố cục vé"
                onClick={() => setLayoutPreviewOpen(true)}
              >
                <i className="ri-drag-move-2-line align-bottom me-1"></i>
                {st('preview_layout_button')}
              </Button>
            )}
          </FormGroup>

          <div className="alert alert-info">
            <i className="ri-information-line"></i> {st('note_title')}
            <ul className="mb-0 mt-2">
              <li>{st('note_auto_code')}</li>
              <li>{st('note_initial_status')}</li>
              <li>{st('note_file_required')}</li>
            </ul>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={handleClose} disabled={submitting}>
            {common('cancel_button')}
          </Button>
          {PageActionPermission.canDisplay(location.pathname, "button-create-submit") && (
            <Button
              color="primary"
              type="submit"
              data-action-code="button-create-submit"
              data-action-name="Lưu mẫu vé mới"
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  {common('saving_label')}
                </>
              ) : (
                common('save_button')
              )}
            </Button>
          )}
        </ModalFooter>
      </Form>

      {/* Popup xem trước bố cục ngay sau khi chọn ảnh mẫu vé */}
      <Modal isOpen={layoutPreviewOpen} toggle={() => setLayoutPreviewOpen(false)} size="xl" style={{ maxWidth: '95%' }} centered>
        <ModalHeader toggle={() => setLayoutPreviewOpen(false)}>
          {st('preview_layout_modal_title', { fileName: selectedFile?.name })}
        </ModalHeader>
        <ModalBody>
          <div className="alert alert-secondary py-2 mb-3">
            <i className="ri-information-line me-1"></i>
            {st('preview_layout_modal_notice')}
          </div>
          <TicketLayoutEditor imageUrl={previewImageUrl} onLayoutChange={setLayoutJson} />
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setLayoutPreviewOpen(false)}>{st('close_button')}</Button>
        </ModalFooter>
      </Modal>
    </Modal>
  );
};

export default CreateTicketDesignTemplateModal;
